import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import {
	DeleteOutlined,
	EditOutlined,
	FileSearchOutlined,
} from '@ant-design/icons';
import { useForm } from '@/libs/antd/form';
import {
	addCCASiteReportPhoto,
	deleteCCASiteReportPhoto,
	updateCCASiteReportPhoto,
} from '@/libs/fleet-api/services/visit-reports';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { useMutation } from '@tanstack/react-query';
import { FileData, SiteReportPhoto } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { useCCAVisitReportVerificationPage } from './VisitReportVerificationPage';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';

type SiteVisitReportPhoto = SiteReportPhoto<'payload'> &
	Pick<SiteReportPhoto, 'photo'>;

type SiteVisitReportPhotoForm = {
	input?: SiteVisitReportPhoto;
	lists: SiteVisitReportPhoto[];
};

const CCASiteVisitReportPhoto: FC = () => {
	const { modal: modalConfirm, openPreviewModal } = useModals();
	const { surveyInformation, siteReport, mainInformation } =
		useCCAVisitReportVerificationPage();
	const { form, fieldName } = useForm<SiteVisitReportPhotoForm>();
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const addCCASiteReportPhotoMutation = useMutation({
		mutationFn: addCCASiteReportPhoto,
	});

	const updateCCASiteReportPhotoMutation = useMutation({
		mutationFn: updateCCASiteReportPhoto,
	});

	const deleteCCASiteReportPhotoMutation = useMutation({
		mutationFn: deleteCCASiteReportPhoto,
	});

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<Form.Provider>
			<FleetTitle>Foto Survey Site</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists:
							siteReport?.photos?.map((photo) => {
								return {
									id: photo.id,
									description: photo.description,
									photo: {
										id: photo.photo.id,
										name: photo.photo.name,
										path: photo.photo.path,
										url: photo.photo.url,
									},
								};
							}) ?? [],
					} as SiteVisitReportPhotoForm
				}
				onFinish={async ({ input, lists }) => {
					if (modal?.type === 'edit') {
						form.setFieldsValue({
							lists: lists.map((list, index) => {
								return index === modal.index ? input! : list;
							}),
						});
						await updateCCASiteReportPhotoMutation.mutateAsync({
							...input!,
							surveyId: surveyInformation!.id!,
							photo: {
								id: input!.photo.id,
								name: input!.photo.name,
								path: input!.photo.path,
							},
						});
					} else {
						const res = await addCCASiteReportPhotoMutation.mutateAsync({
							...input!,
							surveyId: surveyInformation!.id!,
							photo: {
								id: input!.photo.id,
								name: input!.photo.name,
								path: input!.photo.path,
							},
						});
						form.setFieldsValue({
							lists: [...lists, { ...input!, id: res.data.id }],
						});
					}
					setModal(null);
				}}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as SiteVisitReportPhotoForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Fragment>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										loading={
											addCCASiteReportPhotoMutation.isPending ||
											updateCCASiteReportPhotoMutation.isPending
										}
										onClick={() => setModal({ type: 'add' })}
										icon={'+'}
									>
										Add Another
									</Button>
								</Row>
								<Table
									bordered
									pagination={false}
									dataSource={dataSource}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	openPreviewModal(record.photo as FleetFile);
																}}
															>
																<FileSearchOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const isConfirmed =
																		await modalConfirm.confirm({
																			title: 'Hapus foto ini?',
																		});
																	if (!isConfirmed) return;
																	await deleteCCASiteReportPhotoMutation.mutateAsync(
																		record.id!,
																	);
																	form.setFieldsValue({
																		lists: values.lists.filter(
																			(_, listIndex) => listIndex !== index,
																		),
																	});
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
											width: 1,
										},
										{
											title: 'Description',
											render: (_, record) => record.description ?? '-',
										},
									]}
								/>
								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form.submit();
									}}
								>
									<FleetTitle>Add Foto Survey Site</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'photo'])}
												label="Upload Foto"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => {
													const file = value as FileData;
													return {
														fileList: file?.id
															? ([
																	{
																		uid: file.id,
																		name: file.name,
																		thumbUrl: file.url,
																	},
																] as UploadFile[])
															: [],
													};
												}}
												normalize={({ file }: { file: UploadFile }) => {
													return {
														id: file.uid,
														format: file.type,
														name: file.name,
														url: file.thumbUrl,
													} as FileData;
												}}
											>
												<Upload.Dragger
													listType="picture"
													maxCount={1}
													beforeUpload={() => false}
													onRemove={() => {
														form.setFieldValue(
															fieldName(['input', 'photo']),
															null,
														);
														return false;
													}}
													onChange={async ({ file }) => {
														const fileid = Date.now().toString();
														const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/site-photo`;

														try {
															const url = await uploadMutation.mutateAsync({
																file: file as unknown as File,
																fileid,
																filepath,
															});

															form.setFieldValue(
																fieldName(['input', 'photo']),
																{
																	format: 'jpeg',
																	id: fileid,
																	name: file.name,
																	path: filepath,
																	url,
																},
															);
														} catch {
															form.setFieldValue(
																fieldName(['input', 'photo']),
																null,
															);
														}
													}}
												>
													{uploadMutation.isPending ? (
														<LoadingOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													) : (
														<UploadOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													)}
												</Upload.Dragger>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'description'])}
												label="Keterangan Foto"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default CCASiteVisitReportPhoto;
